import React, { useEffect, useRef, useState } from 'react';
import dropin from 'braintree-web-drop-in';

/**
 * Braintree Drop-in UI component.
 * Props:
 *   clientToken {string}
 *   onReady     {function} — receives { requestPaymentMethod }
 *   onError     {function}
 */
const BraintreeDropIn = ({ clientToken, onReady, onError }) => {
  const instanceRef  = useRef(null);
  const [loading, setLoading] = useState(true);
  const containerId  = 'braintree-dropin-container';

  useEffect(() => {
    if (!clientToken) return;

    let destroyed = false;

    // Teardown any old instance first
    const init = () => {
      const el = document.getElementById(containerId);
      if (!el || destroyed) return;
      el.innerHTML = ''; // clear stale markup

      dropin.create({
        authorization: clientToken,
        container: `#${containerId}`,
        card: {
          cvv: {
            required: true,   // CVV field force enable
          },
          cardholderName: {
            required: false,
          },
          overrides: {
            fields: {
              cvv: {
                placeholder: 'CVV',
              },
            },
          },
        },
        paypal: {
          flow: 'vault'
        },
        paypalCredit: false,
        venmo: false,
      })
        .then((instance) => {
          if (destroyed) { instance.teardown(); return; }
          instanceRef.current = instance;
          setLoading(false);
          onReady && onReady({
            requestPaymentMethod: () => instance.requestPaymentMethod(),
          });
        })
        .catch((err) => {
          if (!destroyed) {
            setLoading(false);
            console.error('Braintree init error:', err);
            onError && onError(err);
          }
        });
    };

    // Teardown existing instance if any
    if (instanceRef.current) {
      instanceRef.current.teardown()
        .catch(() => {})
        .finally(() => {
          instanceRef.current = null;
          setTimeout(init, 50);
        });
    } else {
      setTimeout(init, 50); // small delay for DOM to be ready
    }

    return () => {
      destroyed = true;
      if (instanceRef.current) {
        instanceRef.current.teardown().catch(() => {});
        instanceRef.current = null;
      }
    };
  }, [clientToken]);

  return (
    <div>
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '1.5rem',
          color: '#6b7280',
          fontSize: '0.9rem',
        }}>
          ⏳ Loading secure payment form...
        </div>
      )}
      <div
        id={containerId}
        style={{ minHeight: loading ? 0 : 'auto' }}
      />
    </div>
  );
};

export default BraintreeDropIn;
